import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { merge } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor } from '@progress/kendo-data-query';
import { filter, finalize } from 'rxjs/operators';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { NotificationService } from '@app/core/notifications/notification.service';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { CustomReportsService } from '@app/modules/custom-reports/services/reports.service';
import { CustomReportVerbose, ReportParameter, ReportRun } from '@app/modules/custom-reports/models/custom-report.model';
import { RowActionButtonInput, RowActionEventOutput } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employment-records/components/employment-record-details/components/data-grid-comp/models/data-grid-comp.model';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';
import { ReportParametersDialogComponent } from '@app/modules/custom-reports/components/report-parameters-dialog/report-parameters-dialog.component';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-report-detail',
  templateUrl: './report-detail.component.html',
  styleUrls: ['./report-detail.component.scss']
})
export class ReportDetailComponent implements OnInit {
  public reportId: string;
  public report: CustomReportVerbose;
  public isLoadingReport: boolean;
  public isRunningReport: boolean;

  public runsGridDataResult: GridDataResult;
  public isLoadingRuns: boolean;
  public runsPageSize: number = 10;
  public runsSkip: number = 0;
  public runsFilterString: string;

  public runsColumns: any[] = [
    {
      field: 'status',
      subField: 'name',
      title: 'Status',
      type: 'chip',
      chipValue: [
        'Completed',
        ['Failed', 'Failed With Errors', 'Error'],
        ['Running', 'In Progress'],
        ['Pending', 'Queued'],
        ['Cancelled', 'Deleted']
      ]
    },
    { field: 'requestedAt', title: 'Requested At', type: 'dateTime' },
    { field: 'completedAt', title: 'Completed At', type: 'dateTime' },
    { field: 'runBy', title: 'Run By', type: 'employee' },
    { field: 'numberOfRows', title: 'Rows' },
    { field: 'errorMessage', title: 'Error', type: 'longText' },
  ];

  public runsSort: SortDescriptor[] = [
    { field: 'requestedAt', dir: 'desc' }
  ];

  public runsSortableColumns: any[] = [
    { field: 'status', sortValue: 'status.name' },
    { field: 'requestedAt', sortValue: 'requestedAt' },
    { field: 'completedAt', sortValue: 'completedAt' },
    { field: 'runBy', sortValue: 'runBy.lastName' },
    { field: 'numberOfRows', sortValue: 'numberOfRows' },
  ];
  private runsSortString: string = 'requestedAt-desc';

  public runsFilterCategories: any[] = [
    { field: 'status.name', title: 'Status', dataType: 'String' },
    { field: 'requestedAt', title: 'Requested At', dataType: 'Date' },
    { field: 'completedAt', title: 'Completed At', dataType: 'Date' },
    { field: 'runBy.firstName', title: 'Run By (First Name)', dataType: 'String' },
    { field: 'runBy.lastName', title: 'Run By (Last Name)', dataType: 'String' },
    { field: 'numberOfRows', title: 'Rows', dataType: 'Number' },
  ];

  public runsRowActionButtons: RowActionButtonInput[] = [
    { id: 'view', name: 'View', icon: 'visibility' },
    { id: 'download', name: 'Download PDF', icon: 'download' },
    { id: 'delete', name: 'Delete', icon: 'delete' },
  ];

  private currentCultureId: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private snackbarService: SnackbarService,
    private overlayService: OverlayService,
    private reportsService: CustomReportsService,
    private employeeSecurityService: EmployeeSecurityService,
    private dialog: MatDialog,
    private notificationService: NotificationService,
    private destroyRef: DestroyRef,
  ) { }

  ngOnInit(): void {
    this.reportId = this.route.snapshot.paramMap.get('id');
    this.loadReport();
    this.loadReportRuns();
    this.refreshRunsOnRealtimeUpdates();

    this.employeeSecurityService.getCurrentEmployeeCulture().subscribe(culture => {
      this.currentCultureId = culture?.id;
    });
  }

  get reportName(): string {
    if (!this.report) {
      return 'Report';
    }

    if (typeof this.report.name === 'string') {
      return this.report.name;
    }

    const localizedName = this.report.name?.find(n => n.culture === this.currentCultureId);

    return localizedName?.text ?? this.report.name?.[0]?.text ?? 'Report';
  }

  /** Keep the runs grid current as report runs start, finish or fail on the server. */
  private refreshRunsOnRealtimeUpdates(): void {
    merge(
      this.notificationService.on('reporting.reportrun.started'),
      this.notificationService.on('reporting.reportrun.completed'),
      this.notificationService.on('reporting.reportrun.failed')
    )
      .pipe(
        filter(evt => evt.reportId === this.reportId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.loadReportRuns());
  }

  private loadReport(): void {
    this.isLoadingReport = true;

    this.reportsService.getReportById(this.reportId)
      .pipe(finalize(() => this.isLoadingReport = false))
      .subscribe(report => {
        this.report = report;
      });
  }

  loadReportRuns(): void {
    this.isLoadingRuns = true;

    this.reportsService.getReportRuns(this.reportId, this.runsPageSize, this.runsSkip, this.runsSortString, this.runsFilterString)
      .pipe(finalize(() => this.isLoadingRuns = false))
      .subscribe(
        pagedRuns => {
          this.runsGridDataResult = {
            data: pagedRuns.data,
            total: pagedRuns.total,
          };
        }
      );
  }

  runsPageChange(event: PageChangeEvent): void {
    this.runsSkip = event.skip;
    this.runsPageSize = event.take;
    this.loadReportRuns();
  }

  runsSortChange(sortString: string): void {
    this.runsSortString = sortString || 'requestedAt-desc';
    this.loadReportRuns();
  }

  runsFilterCallback(filterString: string): void {
    this.runsFilterString = filterString;
    this.runsSkip = 0;
    this.loadReportRuns();
  }

  runsRowAction(event: RowActionEventOutput): void {
    const run: ReportRun = event.rowData;

    if (event.id === 'delete') {
      this.deleteRun(run);
      return;
    }

    if (!this.requireCompletedRun(run)) {
      return;
    }

    switch (event.id) {
      case 'download':
        this.downloadRun(run);
        break;
      case 'view':
        this.viewRun(run);
        break;
    }
  }

  onRunRowClick(run: ReportRun): void {
    if (!this.requireCompletedRun(run)) {
      return;
    }

    this.viewRun(run);
  }

  private requireCompletedRun(run: ReportRun): boolean {
    if (run.status?.name !== 'Completed') {
      this.snackbarService.openSnackBar('This report run has not completed yet', 'clear', 'warn');
      return false;
    }

    return true;
  }

  private downloadRun(run: ReportRun): void {
    this.reportsService.getReportRunFile(run.id).subscribe({
      next: (url) => {
        this.triggerFileDownload(url, `${this.reportName}-${run.id}.pdf`);
      },
      error: () => {
        this.snackbarService.openSnackBar('Unable to download report file', 'clear', 'warn');
      }
    });
  }

  private triggerFileDownload(url: string, fileName: string): void {
    // Fetching the bytes ourselves and downloading from a blob: URL (same-origin) guarantees a
    // real "Save As" prompt. Handing the raw url straight to an <a download> tag doesn't work here
    // because it's cross-origin, so the browser ignores the download attribute and just navigates
    // the current tab to it instead.
    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Download failed');
        }
        return response.blob();
      })
      .then(blob => {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
      })
      .catch(() => {
        // If the file can't be fetched directly (e.g. CORS), open it in a new tab rather than
        // navigating away from the report page.
        window.open(url, '_blank');
      });
  }

  private viewRun(run: ReportRun): void {
    this.router.navigate(['/Reports/Runs', run.id]);
  }

  private deleteRun(run: ReportRun): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      text: 'Are you sure you want to delete this report run?'
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed !== true) {
        return;
      }

      this.reportsService.deleteReportRun(run.id).subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report run deleted', 'clear', 'success');
          this.loadReportRuns();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to delete report run', 'clear', 'warn');
        }
      });
    });
  }

  runReport(): void {
    this.overlayService.show();
    this.isRunningReport = true;

    this.reportsService.getReportParameters(this.reportId)
      .pipe(finalize(() => {
        this.isRunningReport = false;
        this.overlayService.hide();
      }))
      .subscribe({
        next: (parameters) => {
          if (parameters?.length) {
            this.openParametersDialog(parameters);
          } else {
            this.startReportRun();
          }
        },
        error: () => {
          this.startReportRun();
        }
      });
  }

  private openParametersDialog(parameters: ReportParameter[]): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      reportName: this.reportName,
      parameters
    };

    const dialogRef = this.dialog.open(ReportParametersDialogComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(values => {
      if (values) {
        this.startReportRun(values);
      }
    });
  }

  private startReportRun(parameterValues?: Record<string, string>): void {
    this.isRunningReport = true;

    this.reportsService.runReport(this.reportId, parameterValues)
      .pipe(finalize(() => this.isRunningReport = false))
      .subscribe({
        next: () => {
          this.snackbarService.openSnackBar('Report run started', 'clear', 'success');
          this.runsSkip = 0;
          this.loadReportRuns();
        },
        error: () => {
          this.snackbarService.openSnackBar('Unable to start report run', 'clear', 'warn');
        }
      });
  }

  backToReportGroups(): void {
    const sanitizedReportType = this.report?.type?.name?.replace(/ /g, '');
    this.router.navigate([`Reports/Type/${sanitizedReportType}`]);
  }
}
